import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import { selectAuthTokenVerification } from 'store/authToken/authToken.selector.ts';
import {
  setupAuthorizationDatabaseAndSaveToken,
  verifyToken,
} from 'store/authToken/authToken.thunk.ts';
import { syncSessionExpiration } from 'store/authToken/authToken.session.ts';
import { Loading, ToastMessage } from 'components/primitive';
import { GetSitePermission } from '@/utilities';
import SitePermissionTypes from '@/common/enum/SitePermissionTypes.ts';
import { SiteStatusCodes, ToastStatus } from '@/common/enum';
import { UserAuthCreds } from 'interfaces/UserAccountInfo.interface.ts';
import { setAuthCreds } from 'store/authToken/authToken.action.ts';
import { setAccount, setLoadingState } from 'store/accountsInfo/accountsInfo.actions.ts';
import { selectAppLoading } from 'store/accountsInfo/accountsInfo.selector.ts';
import useStartHealthChecker from '@/utilities/startHealthChecker.ts';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

// Site status codes that resolve to a matching accessType (ACTIVE + a permission
// letter) never reach this map. Everything else -- active-but-unpermissioned,
// pending, suspended, or an unrecognized/denied status -- gets a message a user
// can act on instead of the same generic "no access" line for every case.
const NO_ACCESS_MESSAGES: Record<string, string> = {
  [SiteStatusCodes.ACTIVE]:
    'Your account is active but has not been assigned a permission level for this application. Contact an administrator.',
  [SiteStatusCodes.REQUEST]: 'Your access request for this application is still pending approval.',
  [SiteStatusCodes.SUSPEND]:
    'Your access to this application has been suspended. Contact an administrator.',
};

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [idTokenProcessed, setIdTokenProcessed] = useState(false);
  const idToken = searchParams.get('id_token');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAppLoading = useAppSelector(selectAppLoading);
  const authTokenVerification = useAppSelector(selectAuthTokenVerification);

  useStartHealthChecker();
  useEffect(() => {
    if (idToken?.length) {
      dispatch(verifyToken(idToken)).then(() => {
        setIdTokenProcessed(true);
      });
    } else {
      setIdTokenProcessed(true);
    }
  }, [idToken, dispatch]);

  useEffect(() => {
    if (!idTokenProcessed) return;
    try {
      if (
        authTokenVerification &&
        authTokenVerification.attributes &&
        authTokenVerification.accessToken
      ) {
        let accessType;
        const groupId = authTokenVerification.pvActiveGroupID;
        const sitePermission = GetSitePermission('DMAM', groupId, authTokenVerification.attributes);
        const permissions = sitePermission.sitePermissions;

        switch (permissions && permissions[0]) {
          case 'A':
            accessType = SitePermissionTypes.A;
            break;
          case 'F':
            accessType = SitePermissionTypes.F;
            break;
          case 'U':
            accessType = SitePermissionTypes.U;
            break;
          case 'L':
            accessType = SitePermissionTypes.L;
            break;
          case 'G':
            accessType = SitePermissionTypes.G;
            break;
          default:
            accessType = null;
        }

        if (!accessType) {
          throw new Error(
            NO_ACCESS_MESSAGES[sitePermission.siteStatus] ??
              "You don't have access to the Damage Assessment Mobile App."
          );
        }

        const userAuthCreds: UserAuthCreds = {
          id: authTokenVerification.accessToken,
          userId: authTokenVerification.id,
          username: authTokenVerification.username,
          accessType: accessType,
        };
        sessionStorage.setItem('userAuthCreds', JSON.stringify(userAuthCreds));
        // The offline auth database is written asynchronously, so this synchronous
        // try/catch never saw its failure: the `return` it guarded was unreachable and
        // a rejected write escaped as an unhandled rejection. Sign-in still proceeds --
        // the session itself lives in Redux and sessionStorage -- but the failure is
        // reported now instead of vanishing.
        setupAuthorizationDatabaseAndSaveToken(userAuthCreds).catch((error) =>
          console.error('Could not write the offline authorization database', error)
        );
        dispatch(setAuthCreds(userAuthCreds));
        // Record when this session dies. Both entry paths land here: the Cognito
        // sign-in (expiry read from the pool's stored session) and the portal
        // hand-off (expiry read from the ?id_token= JWT, which cannot be renewed).
        syncSessionExpiration(idToken).catch((error) =>
          console.warn('Could not record session expiration', error)
        );
        dispatch(setAccount(authTokenVerification));
        dispatch(setLoadingState(false));
        return navigate('/loading');
      } else {
        throw new Error('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.log('Error occured', error);
      dispatch(setLoadingState(false));
      toast.info(
        <ToastMessage
          status={ToastStatus.ERROR}
          message={
            error instanceof Error ? error.message : 'Authentication failed. Please try again.'
          }
        />
      );
      return navigate('/login');
    }
    // dispatch is omitted deliberately (stable, adding it is a no-op); idToken and
    // navigate are omitted because they're not safe to add. AuthPage is a layout
    // route that stays mounted across navigation (see the authpage-forces-loading-
    // route-on-every-mount learning), so the navigate('/loading') call above clears
    // the id_token query param, which flips idToken to null on the next render --
    // if idToken were a dependency, that change (caused by this effect's own
    // action) would re-fire it and duplicate the offline-auth-db write and the
    // dispatches above. Same effect-triggers-itself shape as the bug fixed in
    // LoadingContainer.tsx (loading-flag-as-guard-and-dependency).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokenVerification, idTokenProcessed]);

  if (isAppLoading) {
    return <Loading message={'Authentication in process, please wait...'} />;
  } else {
    return <Outlet />;
  }
};

export default AuthPage;
